import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { ClipboardCheck, Loader2, Search, Plus, X, Check } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";

export default function StockCountPage() {
  const navigate = useNavigate();
  const { can } = useAuth();
  const canCount = can("stock.adjust");
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [lines, setLines] = useState([]); // {product_id, title, recorded, counted}
  const [busy, setBusy] = useState(false);
  const [latest, setLatest] = useState(null);

  useEffect(() => { api.get("/stock-counts/latest").then((r) => setLatest(r.data)).catch(() => {}); }, []);

  const search = async (e) => {
    e?.preventDefault();
    if (!q.trim()) { setResults([]); return; }
    setSearching(true);
    try {
      const r = await api.get("/products", { params: { q: q.trim(), page_size: 15 } });
      setResults(r.data.items || []);
    } catch { toast.error("Search failed"); }
    finally { setSearching(false); }
  };

  const addLine = (p) => {
    if (lines.some((l) => l.product_id === p.legacy_id)) return;
    setLines((s) => [...s, { product_id: p.legacy_id, title: p.title || p.legacy_id, recorded: Number(p.total_stock || 0), counted: Number(p.total_stock || 0) }]);
  };
  const setCounted = (id, v) => setLines((s) => s.map((l) => (l.product_id === id ? { ...l, counted: v } : l)));
  const removeLine = (id) => setLines((s) => s.filter((l) => l.product_id !== id));

  const totals = useMemo(() => {
    let net = 0, varLines = 0;
    lines.forEach((l) => { const d = Number(l.counted || 0) - Number(l.recorded || 0); net += d; if (d !== 0) varLines += 1; });
    return { net, varLines };
  }, [lines]);

  const submit = async () => {
    if (lines.length === 0) { toast.error("Add at least one product"); return; }
    setBusy(true);
    try {
      const payload = { lines: lines.map((l) => ({ ...l, counted: Number(l.counted || 0), recorded: Number(l.recorded || 0) })) };
      await api.post("/stock-counts", payload);
      toast.success(`Count saved — ${totals.varLines} variance(s)`);
      navigate("/");
    } catch (e) { toast.error(e.response?.data?.detail || "Could not save count"); }
    finally { setBusy(false); }
  };

  return (
    <div className="p-6 lg:p-8 max-w-4xl" data-testid="stock-count-page">
      <div className="flex items-center gap-3 mb-6">
        <span className="w-10 h-10 rounded-xl bg-indigo-50 text-indigo-600 flex items-center justify-center"><ClipboardCheck className="w-5 h-5" /></span>
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-slate-900 tracking-tight">Stock count</h1>
          <p className="text-sm text-slate-500 mt-0.5">Count products on the warehouse floor — variances feed the dashboard.</p>
        </div>
      </div>

      {latest?.exists && (
        <div className="mb-4 rounded-xl border border-slate-200 bg-white p-3 text-xs text-slate-500" data-testid="stock-count-latest">
          Last count {latest.created_at?.slice(0, 10)} by {latest.created_by} · {latest.lines_with_variance} variance(s), net {latest.net_variance >= 0 ? "+" : ""}{latest.net_variance}
        </div>
      )}

      {!canCount ? (
        <div className="rounded-2xl border border-dashed border-slate-300 p-10 text-center text-slate-500">You need the “Adjust stock” permission to run a count.</div>
      ) : (
        <>
          <form onSubmit={search} className="flex gap-2 mb-3">
            <div className="relative flex-1"><Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input value={q} onChange={(e) => setQ(e.target.value)} className="pl-8" placeholder="Search product name or SKU…" data-testid="stock-count-search" /></div>
            <Button type="submit" variant="outline" disabled={searching}>{searching ? <Loader2 className="w-4 h-4 animate-spin" /> : "Search"}</Button>
          </form>

          {results.length > 0 && (
            <div className="mb-4 rounded-xl border border-slate-200 divide-y divide-slate-100 bg-white" data-testid="stock-count-results">
              {results.map((p) => (
                <div key={p.legacy_id} className="flex items-center justify-between gap-2 px-3 py-2">
                  <div className="min-w-0"><div className="text-sm text-slate-800 truncate">{p.title}</div>
                    <div className="text-[11px] text-slate-400">Recorded: {p.total_stock ?? 0}{p.sku ? ` · ${p.sku}` : ""}</div></div>
                  <Button size="sm" variant="outline" className="gap-1 shrink-0" onClick={() => addLine(p)} data-testid={`stock-count-add-${p.legacy_id}`}><Plus className="w-3.5 h-3.5" /> Add</Button>
                </div>
              ))}
            </div>
          )}

          <div className="rounded-2xl border border-slate-200 bg-white overflow-hidden">
            <div className="px-4 py-3 border-b border-slate-100 flex items-center justify-between">
              <h2 className="text-sm font-bold text-slate-900">Count sheet ({lines.length})</h2>
              {lines.length > 0 && <span className="text-xs text-slate-500">Net variance <b className={totals.net === 0 ? "text-slate-700" : totals.net < 0 ? "text-rose-600" : "text-emerald-600"}>{totals.net >= 0 ? "+" : ""}{totals.net}</b></span>}
            </div>
            {lines.length === 0 ? (
              <div className="p-8 text-center text-sm text-slate-400">Search and add products to start counting.</div>
            ) : (
              <table className="w-full text-sm">
                <thead className="bg-slate-50 text-left text-xs font-semibold uppercase tracking-wide text-slate-500">
                  <tr><th className="px-4 py-2">Product</th><th className="px-4 py-2 w-24 text-right">Recorded</th><th className="px-4 py-2 w-28">Counted</th><th className="px-4 py-2 w-24 text-right">Variance</th><th className="px-4 py-2 w-10"></th></tr>
                </thead>
                <tbody>
                  {lines.map((l) => {
                    const v = Number(l.counted || 0) - Number(l.recorded || 0);
                    return (
                      <tr key={l.product_id} className="border-t border-slate-100" data-testid={`stock-count-line-${l.product_id}`}>
                        <td className="px-4 py-2 text-slate-800">{l.title}</td>
                        <td className="px-4 py-2 text-right tabular-nums text-slate-500">{l.recorded}</td>
                        <td className="px-4 py-2"><Input type="number" value={l.counted} onChange={(e) => setCounted(l.product_id, e.target.value)} className="h-8 text-right" data-testid={`stock-count-input-${l.product_id}`} /></td>
                        <td className={`px-4 py-2 text-right tabular-nums font-semibold ${v === 0 ? "text-slate-400" : v < 0 ? "text-rose-600" : "text-emerald-600"}`}>{v > 0 ? "+" : ""}{v}</td>
                        <td className="px-4 py-2"><button onClick={() => removeLine(l.product_id)} className="text-slate-400 hover:text-rose-600" data-testid={`stock-count-remove-${l.product_id}`}><X className="w-4 h-4" /></button></td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>

          <div className="mt-4 flex justify-end gap-2">
            <Button variant="outline" onClick={() => navigate("/")}>Cancel</Button>
            <Button className="bg-indigo-600 hover:bg-indigo-700 text-white gap-1.5" disabled={busy || lines.length === 0} onClick={submit} data-testid="stock-count-submit">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />} Save count</Button>
          </div>
        </>
      )}
    </div>
  );
}
